'use strict';
/**
 * ConfigDialog.jsx  v3
 * Agrega campo tolerancia_evento en sección de umbrales de nivel
 */

import { useEffect, useState } from 'react';
import {
    Alert, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControl,
    IconButton, InputLabel, MenuItem, Select, Stack,
    Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { TANKS } from './Hipergas.utils';

function Field({ label, value, path, step = 1, unit = '', helperText = '', onChange }) {
    return (
        <TextField
            label={label}
            type="number"
            size="small"
            fullWidth
            value={value ?? ''}
            inputProps={{ step }}
            InputProps={{
                endAdornment: unit
                    ? <Typography variant="caption" sx={{ ml: 0.5, color: 'text.disabled', whiteSpace: 'nowrap' }}>{unit}</Typography>
                    : undefined,
            }}
            helperText={helperText}
            onChange={(e) => onChange(path, e.target.value)}
            sx={{ mb: 1.5 }}
        />
    );
}

export default function ConfigDialog({ open, apiUrl, tabla = 'alvear', onSave, onClose }) {
    const [local, setLocal] = useState({});
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const tank = TANKS[tab];
    const cfg = local[tank?.id] || {};

    // Cargar config desde BD al abrir
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setError('');
        setSuccess('');
        fetch(`${apiUrl}/api/config/${tabla}`, {
            headers: { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' }
        })
            .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then((rows) => {
                const map = {};
                rows.forEach((r) => { map[r.tanque_id] = r; });
                setLocal(map);
            })
            .catch((err) => setError(`Error cargando configuración: ${err.message}`))
            .finally(() => setLoading(false));
    }, [open, apiUrl, tabla]);

    function upd(path, val) {
        setLocal((prev) => {
            const n = { ...prev };
            const tid = tank.id;
            n[tid] = { ...n[tid] };
            const keys = path.split('.');
            let obj = n[tid];
            for (let i = 0; i < keys.length - 1; i++) {
                obj[keys[i]] = { ...obj[keys[i]] };
                obj = obj[keys[i]];
            }
            const lastKey = keys[keys.length - 1];
            if (lastKey === 'densidad_override') {
                obj[lastKey] = val === '' || val === '0' || val === 0 ? null : Number(val);
            } else {
                obj[lastKey] = val === '' ? null : isNaN(Number(val)) ? val : Number(val);
            }
            return n;
        });
    }

    async function handleSave() {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const promises = TANKS.map((t) => {
                const c = local[t.id];
                if (!c) return Promise.resolve(null);
                return fetch(`${apiUrl}/api/config/${tabla}/${t.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': process.env.REACT_APP_HMI_API_KEY || '',
                    },
                    body: JSON.stringify(c),
                }).then((r) => {
                    if (!r.ok) throw new Error(`Tanque ${t.id}: HTTP ${r.status}`);
                    return r.json();
                });
            });
            const results = await Promise.all(promises);
            const updated = {};
            results.forEach((r) => { if (r) updated[r.tanque_id] = r; });
            setLocal(updated);
            setSuccess('Configuración guardada correctamente');
            onSave(updated);
        } catch (err) {
            setError(`Error guardando: ${err.message}`);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight={600}>
                        Configuración de tanques · {tabla}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
                sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                {TANKS.map((t) => (
                    <Tab key={t.id} label={`T${t.id}`} sx={{ minWidth: 0, fontSize: 12 }} />
                ))}
            </Tabs>

            <DialogContent sx={{ pt: 2 }}>
                {loading && (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size={28} />
                        <Typography variant="caption" color="text.disabled" mt={1}>Cargando...</Typography>
                    </Stack>
                )}
                {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {!loading && success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {!loading && cfg.capacidad_lts !== undefined && (
                    <>
                        {/* Capacidad */}
                        <Typography variant="caption" color="text.disabled"
                            sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                            Producto y capacidad
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel>Producto</InputLabel>
                            <Select value={cfg.producto || 'propano'} label="Producto"
                                onChange={(e) => upd('producto', e.target.value)}>
                                <MenuItem value="propano">Propano</MenuItem>
                                <MenuItem value="butano">Butano</MenuItem>
                                <MenuItem value="otro">Otro</MenuItem>
                            </Select>
                        </FormControl>
                        <Field label={`Capacidad total (litros)`} value={cfg.capacidad_lts}
                            path="capacidad_lts" step={100} unit="lts" onChange={upd} />
                        <Field label="Densidad manual (opcional)" value={cfg.densidad_override ?? ''}
                            path="densidad_override" step={0.001} unit="kg/ltr"
                            helperText="Vacío = calcular por temperatura y producto" onChange={upd} />
                        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                            <InputLabel>Mostrar como</InputLabel>
                            <Select value={cfg.unidad_display || 'tn'} label="Mostrar como"
                                onChange={(e) => upd('unidad_display', e.target.value)}>
                                <MenuItem value="tn">Toneladas (tn)</MenuItem>
                                <MenuItem value="kg">Kilogramos (kg)</MenuItem>
                                <MenuItem value="lts">Litros (lts)</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Umbrales nivel */}
                        <Typography variant="caption" color="text.disabled"
                            sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                            Umbrales de nivel (%)
                        </Typography>
                        <Field label="Crítico — alarma roja" value={cfg.nivel_critico} path="nivel_critico" step={1} unit="%" onChange={upd} />
                        <Field label="Bajo — alarma amarilla" value={cfg.nivel_bajo} path="nivel_bajo" step={1} unit="%" onChange={upd} />
                        <Field label="Alto — alarma amarilla" value={cfg.nivel_alto} path="nivel_alto" step={1} unit="%" onChange={upd} />
                        <Field
                            label="Tolerancia de evento"
                            value={cfg.tolerancia_evento ?? 3.0}
                            path="tolerancia_evento"
                            step={0.5}
                            unit="%"
                            helperText="Variación permitida dentro de un evento antes de cerrarlo (histéresis)"
                            onChange={upd}
                        />

                        <Divider sx={{ my: 1.5 }} />

                        {/* Temperatura */}
                        <Typography variant="caption" color="text.disabled"
                            sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                            Temperatura (°C)
                        </Typography>
                        <Field label="Mínima" value={cfg.temp_min} path="temp_min" step={0.5} unit="°C" onChange={upd} />
                        <Field label="Máxima" value={cfg.temp_max} path="temp_max" step={0.5} unit="°C" onChange={upd} />

                        <Divider sx={{ my: 1.5 }} />

                        {/* Presión */}
                        <Typography variant="caption" color="text.disabled"
                            sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                            Presión (bar)
                        </Typography>
                        <Field label="Mínima" value={cfg.presion_min} path="presion_min" step={0.1} unit="bar" onChange={upd} />
                        <Field label="Máxima" value={cfg.presion_max} path="presion_max" step={0.1} unit="bar" onChange={upd} />
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
                <Button onClick={onClose} color="inherit" size="small" disabled={saving}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    size="small"
                    disabled={loading || saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                >
                    {saving ? 'Guardando...' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}